import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { leadpagesAPI } from '../services/api';
import LeadpagePreviewModal, { formatLabel } from '../components/LeadpagePreviewModal';
import './Leadpages.css';

const POLL_INTERVAL_MS = 2500;

const VERTICAL_ICONS = {
    finance: '💰',
    crypto: '🪙',
    tech: '🖥️',
    health: '🩺',
    other: '📰'
};


function PackGallery({ pages, onPreview, onToggleTake, onGenerateMore, generating }) {
    const ready = pages.filter((p) => p.status === 'draft' || p.status === 'selected');
    const taken = pages.filter((p) => p.status === 'selected');
    return (
        <div className="lp-plan glass">
            <div className="lp-plan-header">
                <h3>Generated pack</h3>
                <span className="lp-plan-meta">
                    {ready.length}/{pages.length} ready · {taken.length} taken
                </span>
            </div>
            <div className="lp-plan-items">
                {pages.map((page) => (
                    <div
                        key={page.id}
                        className={`lp-plan-item lp-gallery-card ${page.status === 'selected' ? 'taken' : ''}`}
                    >
                        <div className="lp-plan-item-head">
                            <strong>{formatLabel(page.format_key)}</strong>
                            {page.status === 'generating' && <span className="lp-spinner lp-spinner-sm" />}
                            {page.status === 'failed' && <span className="lp-group-badge">failed</span>}
                            {page.status === 'selected' && <span className="lp-group-badge lp-taken-badge">✓ taken</span>}
                        </div>
                        {page.headline && <p>«{page.headline}»</p>}
                        {page.status === 'failed' && page.status_message && (
                            <p className="lp-error">{page.status_message}</p>
                        )}
                        {(page.status === 'draft' || page.status === 'selected') && (
                            <div className="lp-card-actions">
                                <button className="btn-secondary" onClick={() => onPreview(page)}>
                                    Preview
                                </button>
                                <label className="lp-take-checkbox">
                                    <input
                                        type="checkbox"
                                        checked={page.status === 'selected'}
                                        onChange={() => onToggleTake(page)}
                                    />
                                    Take
                                </label>
                            </div>
                        )}
                    </div>
                ))}
            </div>
            <div className="lp-gallery-footer">
                <button className="btn-secondary" onClick={onGenerateMore} disabled={generating}>
                    {generating ? 'Generating…' : 'Generate more variants'}
                </button>
            </div>
        </div>
    );
}

function PackPlan({ plan }) {
    const persona = plan.persona || {};
    const font = persona.font_pairing || {};
    return (
        <div className="lp-plan glass">
            <div className="lp-plan-header">
                <h3>Pack plan</h3>
                <span className="lp-plan-meta">
                    ordered {plan.ordered} → offering {plan.pack_size}
                </span>
            </div>

            <div className="lp-persona">
                <span className="lp-item-label">Designer persona (fixed for this blog)</span>
                <div className="lp-persona-grid">
                    <div className="lp-profile-item">
                        <span className="lp-item-label">Code style</span>
                        <span>{persona.stack?.description}</span>
                    </div>
                    <div className="lp-profile-item">
                        <span className="lp-item-label">Fonts</span>
                        <span>{font.heading} / {font.body}</span>
                    </div>
                    <div className="lp-profile-item">
                        <span className="lp-item-label">Layout habit</span>
                        <span>{persona.section_habit}</span>
                    </div>
                    <div className="lp-profile-item">
                        <span className="lp-item-label">Form copy tone</span>
                        <span>{persona.form_copy_tone}</span>
                    </div>
                </div>
                <div className="lp-fonts">
                    {(persona.visual_quirks || []).map((quirk) => (
                        <span key={quirk} className="lp-font-chip">{quirk}</span>
                    ))}
                </div>
            </div>

            <div className="lp-plan-items">
                {(plan.items || []).map((item) => (
                    <div key={item.format.key} className="lp-plan-item">
                        <div className="lp-plan-item-head">
                            <strong>{item.format.name}</strong>
                            <span className="lp-group-badge">{item.format.group}</span>
                        </div>
                        <p>{item.format.description}</p>
                        <div className="lp-form-spec">
                            {(item.form_spec.fields || []).map((field) => (
                                <span key={field} className="lp-field-chip">{field}</span>
                            ))}
                            {(item.form_spec.checkboxes || []).map((cb) => (
                                <span key={cb.key} className="lp-field-chip lp-field-required">☑ {cb.label}</span>
                            ))}
                        </div>
                        {item.form_spec.disclaimer && (
                            <span className="lp-disclaimer">{item.form_spec.disclaimer}</span>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
}

function ProfileCard({ source, onReanalyze, onPlan, planning, onGenerate, generating }) {
    const profile = source.profile || {};
    return (
        <div className="lp-profile-card glass">
            <div className="lp-profile-header">
                <span className="lp-vertical-badge">
                    {VERTICAL_ICONS[source.vertical] || '📰'} {source.vertical || 'unknown'}
                </span>
                <h2>{source.title || source.url}</h2>
                <a href={source.url} target="_blank" rel="noopener noreferrer" className="lp-source-url">
                    {source.url}
                </a>
            </div>

            {profile.topic_summary && (
                <p className="lp-topic-summary">{profile.topic_summary}</p>
            )}

            <div className="lp-profile-grid">
                {profile.tone && (
                    <div className="lp-profile-item">
                        <span className="lp-item-label">Tone</span>
                        <span>{profile.tone}</span>
                    </div>
                )}
                {profile.audience && (
                    <div className="lp-profile-item">
                        <span className="lp-item-label">Audience</span>
                        <span>{profile.audience}</span>
                    </div>
                )}
                {profile.content_style && (
                    <div className="lp-profile-item">
                        <span className="lp-item-label">Content style</span>
                        <span>{profile.content_style}</span>
                    </div>
                )}
                {profile.language && (
                    <div className="lp-profile-item">
                        <span className="lp-item-label">Language</span>
                        <span>{profile.language}</span>
                    </div>
                )}
            </div>

            {(profile.palette || []).length > 0 && (
                <div className="lp-palette">
                    <span className="lp-item-label">Palette</span>
                    <div className="lp-swatches">
                        {profile.palette.map((color) => (
                            <span key={color} className="lp-swatch" title={color}>
                                <span className="lp-swatch-color" style={{ backgroundColor: color }} />
                                <span className="lp-swatch-hex">{color}</span>
                            </span>
                        ))}
                    </div>
                </div>
            )}

            {(profile.fonts || []).length > 0 && (
                <div className="lp-fonts">
                    <span className="lp-item-label">Fonts</span>
                    {profile.fonts.map((font) => (
                        <span key={font} className="lp-font-chip">{font}</span>
                    ))}
                </div>
            )}

            {(profile.example_headlines || []).length > 0 && (
                <div className="lp-headlines">
                    <span className="lp-item-label">Example headlines</span>
                    <ul>
                        {profile.example_headlines.map((headline, idx) => (
                            <li key={idx}>{headline}</li>
                        ))}
                    </ul>
                </div>
            )}

            <div className="lp-profile-actions">
                <button className="btn-secondary" onClick={() => onReanalyze(source)}>
                    Re-analyze
                </button>
                <button className="btn-secondary" onClick={() => onPlan(source)} disabled={planning}>
                    {planning ? 'Planning…' : 'Plan pack'}
                </button>
                <button className="btn-primary" onClick={() => onGenerate(source)} disabled={generating}>
                    {generating ? 'Generating…' : 'Generate pack'}
                </button>
            </div>
        </div>
    );
}

function Leadpages() {
    const [url, setUrl] = useState('');
    const [count, setCount] = useState(1);
    const [sources, setSources] = useState([]);
    const [activeId, setActiveId] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [plan, setPlan] = useState(null);
    const [planning, setPlanning] = useState(false);
    const [pages, setPages] = useState([]);
    const [generating, setGenerating] = useState(false);
    const [preview, setPreview] = useState(null);
    const pollRef = useRef(null);
    const pagesPollRef = useRef(null);

    const activeSource = sources.find((s) => s.id === activeId) || null;

    const loadSources = useCallback(async () => {
        try {
            const res = await leadpagesAPI.getSources();
            setSources(res.data);
            return res.data;
        } catch {
            return [];
        }
    }, []);

    useEffect(() => {
        loadSources().then((list) => {
            if (list.length > 0) setActiveId(list[0].id);
        });
        return () => clearInterval(pollRef.current);
    }, [loadSources]);

    useEffect(() => {
        clearInterval(pollRef.current);
        if (activeSource?.status === 'analyzing') {
            pollRef.current = setInterval(async () => {
                try {
                    const res = await leadpagesAPI.getSource(activeId);
                    setSources((prev) => prev.map((s) => (s.id === activeId ? res.data : s)));
                } catch {
                    clearInterval(pollRef.current);
                }
            }, POLL_INTERVAL_MS);
        }
        return () => clearInterval(pollRef.current);
    }, [activeId, activeSource?.status]);

    const submit = async (submitUrl, submitCount) => {
        setError(null);
        setSubmitting(true);
        try {
            const res = await leadpagesAPI.createSource({ url: submitUrl, desired_count: submitCount });
            await loadSources();
            setActiveId(res.data.id);
            setUrl('');
        } catch (e) {
            setError(e.response?.data?.detail?.[0]?.msg || e.response?.data?.detail || 'Failed to submit the blog URL');
        } finally {
            setSubmitting(false);
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!url.trim()) return;
        submit(url.trim(), count);
    };

    const handleDelete = async (source) => {
        await leadpagesAPI.deleteSource(source.id);
        const list = await loadSources();
        if (activeId === source.id) setActiveId(list[0]?.id || null);
    };

    const handlePlan = async (source) => {
        setPlanning(true);
        setError(null);
        try {
            const res = await leadpagesAPI.planPack(source.id);
            setPlan(res.data);
        } catch (e) {
            setError(e.response?.data?.detail || 'Failed to build the pack plan');
        } finally {
            setPlanning(false);
        }
    };

    useEffect(() => {
        setPlan(null);
        setPages([]);
        setPreview(null);
        if (activeId) {
            leadpagesAPI.getLeadpages(activeId)
                .then((res) => setPages(res.data))
                .catch(() => setPages([]));
        }
    }, [activeId]);

    useEffect(() => {
        clearInterval(pagesPollRef.current);
        if (activeId && pages.some((p) => p.status === 'generating')) {
            pagesPollRef.current = setInterval(async () => {
                try {
                    const res = await leadpagesAPI.getLeadpages(activeId);
                    setPages(res.data);
                } catch {
                    clearInterval(pagesPollRef.current);
                }
            }, 4000);
        }
        return () => clearInterval(pagesPollRef.current);
    }, [activeId, pages]);

    const handleGenerate = async (source) => {
        setGenerating(true);
        setError(null);
        try {
            await leadpagesAPI.generatePack(source.id);
            const res = await leadpagesAPI.getLeadpages(source.id);
            setPages(res.data);
        } catch (e) {
            setError(e.response?.data?.detail || 'Failed to start pack generation');
        } finally {
            setGenerating(false);
        }
    };

    const handlePreview = async (page) => {
        try {
            const res = await leadpagesAPI.getLeadpageHtml(page.id);
            setPreview({ page, html: res.data });
        } catch {
            setError('Failed to load the preview');
        }
    };

    const handlePageUpdate = useCallback((updated) => {
        setPages((prev) => prev.map((p) => (p.id === updated.id ? updated : p)));
        setPreview((prev) => (prev && prev.page.id === updated.id ? { ...prev, page: updated } : prev));
    }, []);

    const handleToggleTake = async (page) => {
        const next = page.status === 'selected' ? 'draft' : 'selected';
        try {
            const res = await leadpagesAPI.updateLeadpage(page.id, { status: next });
            setPages((prev) => prev.map((p) => (p.id === page.id ? res.data : p)));
            setPreview((prev) => (prev && prev.page.id === page.id ? { ...prev, page: res.data } : prev));
        } catch {
            setError('Failed to update the selection');
        }
    };

    return (
        <div className="leadpages-page">
            <div className="lp-header">
                <h1><span className="gradient-text">Leadpages</span></h1>
                <p>Paste your beehiiv blog link — get landing pages in its style</p>
                <Link to="/leadpages/manage" className="btn-secondary lp-manage-link">
                    🗂 Manage all leadpages
                </Link>
            </div>

            <form className="lp-form glass" onSubmit={handleSubmit}>
                <div className="lp-form-row">
                    <input
                        type="url"
                        value={url}
                        onChange={(e) => setUrl(e.target.value)}
                        placeholder="https://yourblog.beehiiv.com"
                        required
                        disabled={submitting}
                    />
                    <label className="lp-count-label">
                        Leadpages
                        <input
                            type="number"
                            min={1}
                            max={4}
                            value={count}
                            onChange={(e) => setCount(Math.max(1, Math.min(4, Number(e.target.value) || 1)))}
                            disabled={submitting}
                        />
                    </label>
                    <button type="submit" className="btn-primary" disabled={submitting || !url.trim()}>
                        {submitting ? 'Submitting…' : 'Analyze blog'}
                    </button>
                </div>
                {error && <p className="lp-error">{String(error)}</p>}
            </form>

            {activeSource?.status === 'analyzing' && (
                <div className="lp-analyzing glass">
                    <div className="lp-spinner" />
                    <div>
                        <strong>Reading the blog…</strong>
                        <p>Topic, tone, palette, fonts and audience — usually takes under a minute.</p>
                    </div>
                </div>
            )}

            {activeSource?.status === 'failed' && (
                <div className="lp-failed glass">
                    <p>⚠️ Analysis failed: {activeSource.status_message || 'unknown error'}</p>
                    <button className="btn-secondary" onClick={() => submit(activeSource.url, activeSource.desired_count)}>
                        Retry
                    </button>
                </div>
            )}

            {activeSource?.status === 'ready' && (
                <ProfileCard
                    source={activeSource}
                    onReanalyze={(s) => submit(s.url, s.desired_count)}
                    onPlan={handlePlan}
                    planning={planning}
                    onGenerate={handleGenerate}
                    generating={generating || pages.some((p) => p.status === 'generating')}
                />
            )}

            {activeSource?.status === 'ready' && pages.length > 0 && (
                <PackGallery
                    pages={pages}
                    onPreview={handlePreview}
                    onToggleTake={handleToggleTake}
                    onGenerateMore={() => handleGenerate(activeSource)}
                    generating={generating || pages.some((p) => p.status === 'generating')}
                />
            )}

            {preview && (
                <LeadpagePreviewModal
                    key={preview.page.id}
                    page={preview.page}
                    html={preview.html}
                    onClose={() => setPreview(null)}
                    onToggleTake={handleToggleTake}
                    onPageUpdate={handlePageUpdate}
                />
            )}

            {activeSource?.status === 'ready' && plan && plan.source_id === activeId && (
                <PackPlan plan={plan} />
            )}

            {sources.length > 0 && (
                <div className="lp-sources-list">
                    <h3>Analyzed blogs</h3>
                    {sources.map((source) => (
                        <div
                            key={source.id}
                            className={`lp-source-row glass ${source.id === activeId ? 'active' : ''}`}
                            onClick={() => setActiveId(source.id)}
                        >
                            <span className={`lp-status-dot ${source.status}`} />
                            <span className="lp-source-title">{source.title || source.url}</span>
                            <span className="lp-source-vertical">{source.vertical || ''}</span>
                            <button
                                className="lp-delete-btn"
                                title="Delete"
                                onClick={(e) => { e.stopPropagation(); handleDelete(source); }}
                            >
                                ✕
                            </button>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

export default Leadpages;
